/**
 * Santa Voicemail Form Handler - V2
 * ==================================
 * Updates from V1:
 * - Tracks vm_id returned from backend after preview
 * - Requires delivery_date (no more optional)
 * - Removes phone number (email-only delivery)
 * - Adds vm_id + delivery_date as line item properties on add to cart
 * - Shows add-to-cart button after successful preview
 * - Includes retry logic for network issues (QUIC protocol errors)
 */

(function() {
  'use strict';
  
  console.log('🎅 Santa Voicemail script loading (V2)...');

  // Store the current voicemail ID after preview
  let currentVoicemailId = null;

  function initVoicemailForm() {
    console.log('🎅 Initializing Santa Voicemail form...');
    
    // Form elements
    const form = document.getElementById('ktwa-form');
    const previewBtn = document.getElementById('ktwa-preview-btn');
    const addToCartBtn = document.getElementById('ktwa-add-to-cart');
    const statusEl = document.getElementById('ktwa-status');
    const audioEl = document.getElementById('vm-audio');
    const placeholderEl = document.getElementById('preview-placeholder');
    const praiseEl = document.getElementById('praise');
    const encourageEl = document.getElementById('encourage');
    const praiseCount = document.getElementById('praise-count');
    const encourageCount = document.getElementById('encourage-count');
    const deliveryDate = document.getElementById('delivery_date');

    console.log('Elements found:', {
      form: !!form,
      previewBtn: !!previewBtn,
      addToCartBtn: !!addToCartBtn,
      audioEl: !!audioEl,
      deliveryDate: !!deliveryDate
    });

    if (!previewBtn) {
      console.error('❌ Preview button not found! Make sure the form section is on the page.');
      return;
    }

    const WORKER_ENDPOINT = 'https://openai-api-key.brian-181.workers.dev/voicemail/generate';
    
    // Get the variant ID from the page (set this in your Liquid template)
    const VOICEMAIL_VARIANT_ID = window.SANTA_VOICEMAIL_VARIANT_ID || null;

    // Character counters
    if (praiseEl && praiseCount) {
      praiseEl.addEventListener('input', function() {
        praiseCount.textContent = praiseEl.value.length;
      });
    }
    
    if (encourageEl && encourageCount) {
      encourageEl.addEventListener('input', function() {
        encourageCount.textContent = encourageEl.value.length;
      });
    }

    // Delivery date setup - REQUIRED field
    if (deliveryDate) {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const fmt = function(d) { return d.toISOString().split('T')[0]; };
        
        // Min = today, Max = Dec 24 of current year (or next year if past Dec 24)
        const today = new Date(year, now.getMonth(), now.getDate());
        let maxDate = new Date(year, 11, 24); // Dec 24
        if (now > maxDate) {
          maxDate = new Date(year + 1, 11, 24); // Next year's Dec 24
        }
        
        deliveryDate.min = fmt(today);
        deliveryDate.max = fmt(maxDate);
        
        // Default to today
        deliveryDate.value = fmt(today);
      } catch (e) {
        console.error('Date setup error:', e);
      }
    }

    function showStatus(msg, type) {
      if (statusEl) {
        statusEl.textContent = msg;
        statusEl.className = 'ktwa-status ' + (type || '');
      }
    }

    function sanitize(str) { 
      return (str || '').replace(/\s+/g, ' ').trim(); 
    }

    function base64ToBlob(base64, mimeType) {
      try {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
      } catch (e) {
        console.error('base64ToBlob error:', e);
        return null;
      }
    }

    function showAddToCart() {
      const cartSection = document.getElementById('ktwa-cart-section');
      if (cartSection) {
        cartSection.style.display = 'block';
      }
      if (addToCartBtn) {
        addToCartBtn.style.display = 'block';
        addToCartBtn.disabled = false;
      }
    }

    function hideAddToCart() {
      const cartSection = document.getElementById('ktwa-cart-section');
      if (cartSection) {
        cartSection.style.display = 'none';
      }
      if (addToCartBtn) {
        addToCartBtn.style.display = 'none';
      }
    }

    // Fetch with retry logic for QUIC/network issues
    async function fetchWithRetry(url, options, maxRetries = 3) {
      let lastError;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`📡 Attempt ${attempt}/${maxRetries}...`);
          const response = await fetch(url, options);
          return response;
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ Attempt ${attempt} failed:`, error.message);
          
          // Only retry on network errors, not HTTP errors
          if (attempt < maxRetries) {
            // Wait a bit before retrying (exponential backoff)
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            console.log(`⏳ Retrying in ${delay}ms...`);
            showStatus(`Connection issue, retrying... (${attempt}/${maxRetries})`, '');
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      throw lastError;
    }

    // Initially hide add-to-cart until preview is generated
    hideAddToCart();

    // ============================================================
    // PREVIEW GENERATION
    // ============================================================
    previewBtn.addEventListener('click', async function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      console.log('🎅 Preview button clicked!');
      
      showStatus('', '');
      hideAddToCart();
      currentVoicemailId = null;

      // Gather form data
      const childName = sanitize(document.getElementById('child_name').value);
      const praise = sanitize(praiseEl.value);
      const parentEmail = sanitize(document.getElementById('parent_email').value);
      const selectedDeliveryDate = deliveryDate ? deliveryDate.value : null;

      // Validation
      if (!childName) {
        showStatus("Please enter the child's name.", 'err');
        return;
      }
      if (!praise) {
        showStatus("Please add something to praise.", 'err');
        return;
      }
      if (!parentEmail || !/^\S+@\S+\.\S+$/.test(parentEmail)) {
        showStatus("Please enter a valid email address.", 'err');
        return;
      }
      if (!selectedDeliveryDate) {
        showStatus("Please select a delivery date.", 'err');
        return;
      }

      // Validate date is within allowed range
      const selectedDate = new Date(selectedDeliveryDate + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(today.getFullYear(), 11, 24); // Dec 24
      if (today > maxDate) {
        maxDate.setFullYear(maxDate.getFullYear() + 1);
      }
      
      if (selectedDate < today) {
        showStatus("Please select today or a future date.", 'err');
        return;
      }
      if (selectedDate > maxDate) {
        showStatus("Please select a date on or before December 24th.", 'err');
        return;
      }

      previewBtn.disabled = true;
      previewBtn.textContent = 'Generating...';
      showStatus('Santa is recording your message...', '');
      if (placeholderEl) {
        placeholderEl.textContent = 'Santa is recording your message...';
      }

      // Parse city/state
      const cityStateEl = document.getElementById('city_state');
      const cityState = cityStateEl ? sanitize(cityStateEl.value) : '';
      let city = '';
      let state = '';
      if (cityState) {
        const parts = cityState.split(',').map(p => p.trim());
        city = parts[0] || '';
        state = parts[1] || '';
      }

      const payload = {
        voicemail: {
          child_name: childName,
          pronunciation: sanitize(document.getElementById('name_pronunciation')?.value || ''),
          city: city,
          state: state,
          praise: praise,
          encourage: sanitize(encourageEl?.value || ''),
          parent_email: parentEmail,
          delivery_date: selectedDeliveryDate
        }
      };

      console.log('📡 Calling Worker API...', payload);

      try {
        // Use fetchWithRetry instead of regular fetch
        const response = await fetchWithRetry(WORKER_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }, 3);

        const result = await response.json();
        console.log('Response:', result);

        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to generate voicemail');
        }

        // Store the voicemail ID for checkout
        currentVoicemailId = result.vm_id;
        console.log('✅ Voicemail ID:', currentVoicemailId);

        // Convert base64 to blob and play
        const audioBlob = base64ToBlob(result.audio, 'audio/mpeg');
        if (!audioBlob) {
          throw new Error('Failed to convert audio data');
        }
        
        const audioUrl = URL.createObjectURL(audioBlob);

        if (audioEl) {
          audioEl.src = audioUrl;
          audioEl.style.display = 'block';
        }
        
        if (placeholderEl) {
          placeholderEl.style.display = 'none';
        }

        showStatus('Preview ready! Listen above, then add to cart.', 'ok');
        showAddToCart();

        // Auto-play
        if (audioEl) {
          audioEl.play().catch(err => {
            console.log('Autoplay prevented:', err);
          });
        }

      } catch (error) {
        console.error('❌ Preview error:', error);
        showStatus(error.message || 'Failed to generate preview. Please try again.', 'err');
        if (placeholderEl) {
          placeholderEl.textContent = 'Fill out the form and click "Generate Preview" to hear Santa\'s message.';
          placeholderEl.style.display = 'block';
        }
      } finally {
        previewBtn.disabled = false;
        previewBtn.textContent = 'Generate Preview';
      }
    });

    // ============================================================
    // ADD TO CART
    // ============================================================
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', async function(e) {
        e.preventDefault();

        if (!currentVoicemailId) {
          showStatus('Please generate a preview first.', 'err');
          return;
        }

        if (!VOICEMAIL_VARIANT_ID) {
          console.error('VOICEMAIL_VARIANT_ID not set');
          showStatus('Configuration error. Please refresh and try again.', 'err');
          return;
        }

        const childName = sanitize(document.getElementById('child_name').value);
        const selectedDeliveryDate = deliveryDate ? deliveryDate.value : null;

        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding...';

        try {
          // Add to Shopify cart with line item properties
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: VOICEMAIL_VARIANT_ID,
              quantity: 1,
              properties: {
                'vm_id': currentVoicemailId,
                'Child Name': childName,
                'delivery_date': selectedDeliveryDate,
                'Delivery Date': formatDateForDisplay(selectedDeliveryDate)
              }
            })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          const cartItem = await response.json();
          console.log('✅ Added to cart:', cartItem);

          showStatus('Added to cart! Redirecting...', 'ok');

          // Redirect to cart after short delay
          setTimeout(() => {
            window.location.href = '/cart';
          }, 1000);

        } catch (error) {
          console.error('❌ Add to cart error:', error);
          showStatus('Failed to add to cart. Please try again.', 'err');
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'Add to Cart';
        }
      });
    }

    // Helper to format date for display in cart
    function formatDateForDisplay(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric',
          year: 'numeric'
        });
      } catch (e) {
        return dateStr;
      }
    }

    console.log('🎄 Santa Voicemail form fully initialized (V2)!');
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVoicemailForm);
  } else {
    initVoicemailForm();
    // Retry for Shopify's lazy loading
    setTimeout(initVoicemailForm, 1000);
  }
})();